import { useCallback, useState } from 'react';
import SemanticTableService from '../../services/SemanticTableService';

/**
 * Encapsula el patrón de job asíncrono de table_metadata_generator
 * (generateMetadata -> poll de GET /table-metadata/jobs/{job_id}) con su
 * estado de progreso/error -- antes duplicado entre SemanticTableFormDialog
 * (botón "Generar con IA"/"Regenerar con IA") y TableSelectorDialog
 * ("Import from Database"). `run` devuelve el resultado o `null` si falló
 * (el error queda en `error` para que el caller lo muestre), nunca relanza.
 */
/**
 * true si el job terminó pero SIN IA: cuando Bedrock falla,
 * table_metadata_generator igual completa el job y devuelve los metadatos
 * técnicos de respaldo, avisando solo en `warnings` (ver app.py,
 * "AI description generation failed..."). Para quien pidió explícitamente
 * a la IA eso es un error, no una propuesta vacía.
 */
export const aiGenerationFailed = (result) => (
  result?.ai_generated === false
  || (Array.isArray(result?.warnings) && result.warnings.some((w) => /AI description generation failed/i.test(String(w))))
);

export const AI_UNAVAILABLE_MESSAGE =
  'La IA no pudo generar una propuesta: el servicio de IA (Amazon Bedrock) rechazó la solicitud. '
  + 'Inténtalo más tarde; si continúa, contacta a soporte.';

const useMetadataGenerationJob = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState(null);

  const run = useCallback(async (payload) => {
    setError(null);
    setIsRunning(true);
    setProgressMessage('Iniciando generación de metadata...');

    try {
      const result = await SemanticTableService.generateMetadataWithPolling(
        payload,
        (job, attempts) => {
          if (job.status === 'pending') {
            setProgressMessage('Esperando inicio del procesamiento...');
          } else if (job.status === 'processing') {
            const elapsed = Math.floor(attempts * 3);
            setProgressMessage(`Generando metadata con IA (${elapsed}s)...`);
          }
        }
      );
      return result;
    } catch (err) {
      setError(err.message || 'No se pudo generar la metadata con IA');
      return null;
    } finally {
      setIsRunning(false);
      setProgressMessage('');
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /** Para validaciones previas a `run` (ej. "falta el nombre de tabla") que quieren mostrarse igual que un error de job. */
  const reportError = useCallback((message) => setError(message), []);

  /** Fuerza el estado a inactivo -- para cuando el caller se cierra/desmonta mientras corre un job. */
  const reset = useCallback(() => {
    setIsRunning(false);
    setProgressMessage('');
    setError(null);
  }, []);

  return { run, isRunning, progressMessage, error, clearError, reportError, reset };
};

export default useMetadataGenerationJob;
